import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, LogOut, Mail, CheckCircle, AlertCircle, Settings, ArrowLeft, Copy } from 'lucide-react';

const EmailSettings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [emailStatus, setEmailStatus] = useState({ is_configured: false, has_refresh_token: false });
  
  const [config, setConfig] = useState({
    client_id: '982574795973-sjt3or4m6c8rtkfa4s96ssivdmr7d5i9.apps.googleusercontent.com',
    client_secret: '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',
    redirect_uri: `${window.location.origin}/api/admin/email/callback`
  });
  
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchEmailStatus();
    
    // Check for success/error from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success')) {
      setMessage({ type: 'success', text: 'Gmail OAuth configured successfully! You can now send emails.' });
      // Clean up URL
      window.history.replaceState({}, '', '/admin/email-settings');
    } else if (urlParams.get('error')) {
      setMessage({ type: 'error', text: `OAuth failed: ${urlParams.get('error')}` });
      // Clean up URL
      window.history.replaceState({}, '', '/admin/email-settings');
    }
  }, []);

  const fetchEmailStatus = async () => {
    try {
      const response = await api.get('/admin/email/status');
      setEmailStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch email status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorize = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      const response = await api.post('/admin/email/configure', config);
      
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to initiate OAuth flow'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }

    try {
      setTesting(true);
      setMessage(null);
      
      await api.post('/admin/email/test', { to_email: testEmail });
      
      setMessage({
        type: 'success',
        text: `Test email sent to ${testEmail}. Please check your inbox.`
      });
      setTestEmail('');
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to send test email'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBackToAdmin = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black text-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://cdn.slicksignature.com/landing/slicksignature-logo.png" 
                alt="SlickSignature"
                className="h-12"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<h1 class="text-2xl font-bold">SlickSignature</h1>';
                }}
              />
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-300">
                {user?.email}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-gray-800"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="outline"
          onClick={handleBackToAdmin}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Portal
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <Mail className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Email Configuration</CardTitle>
                <CardDescription>
                  Configure Gmail SMTP with OAuth2 for sending emails
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status Banner */}
            {emailStatus.is_configured ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Email service is configured and ready to send emails from hello@slicksignature.com
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  Email service is not configured. Please authorize Gmail access below.
                </AlertDescription>
              </Alert>
            )}

            {/* Message Alert */}
            {message && (
              <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                {message.type === 'success' ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                )}
                <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                  {message.text}
                </AlertDescription>
              </Alert>
            )}

            {/* OAuth Configuration */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-4">Gmail OAuth Configuration</h3>
                <p className="text-sm text-gray-600 mb-4">
                  The OAuth credentials are pre-configured. Click the button below to authorize SlickSignature to send emails from hello@slicksignature.com
                </p>
              </div>

              {/* Google OAuth Setup Instructions */}
              <Alert className="bg-blue-50 border-blue-200">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <p className="font-semibold mb-2">Before authorizing, add this Redirect URI to your Google OAuth Client:</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <code className="flex-1 bg-blue-100 px-3 py-2 rounded text-xs break-all">
                      {window.location.origin}/api/admin/email/callback
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/api/admin/email/callback`);
                        setMessage({ type: 'success', text: 'Redirect URI copied to clipboard!' });
                      }}
                      className="flex-shrink-0"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs mt-2">
                    Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Google Cloud Console</a> → OAuth Client → Add to "Authorized redirect URIs"
                  </p>
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <div>
                  <Label>Sender Email</Label>
                  <Input
                    value="hello@slicksignature.com"
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    All emails will be sent from this address
                  </p>
                </div>

                <div>
                  <Label>Status</Label>
                  <div className="flex items-center space-x-2 mt-2">
                    <div className={`h-3 w-3 rounded-full ${emailStatus.is_configured ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="text-sm">
                      {emailStatus.is_configured ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleAuthorize}
                disabled={saving}
                className="w-full"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Authorizing...
                  </>
                ) : (
                  <>
                    <Settings className="h-4 w-4 mr-2" />
                    {emailStatus.is_configured ? 'Re-authorize Gmail Access' : 'Authorize Gmail Access'}
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500">
                You will be redirected to Google to sign in and grant permissions to send emails.
              </p>
            </div>

            {/* Test Email Section */}
            {emailStatus.is_configured && (
              <div className="border-t pt-6 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Test Email</h3>
                  <p className="text-sm text-gray-600">
                    Send a test email to verify your configuration is working
                  </p>
                </div>

                <div className="flex space-x-3">
                  <div className="flex-1">
                    <Input
                      type="email"
                      placeholder="Enter test email address"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                    />
                  </div>
                  <Button
                    onClick={handleTestEmail}
                    disabled={testing || !testEmail}
                  >
                    {testing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Send Test
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="border-t pt-6">
              <h3 className="text-sm font-semibold mb-3">How it works:</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                <li>Click "Authorize Gmail Access" to start the OAuth flow</li>
                <li>Sign in with the Google account for hello@slicksignature.com</li>
                <li>Grant permissions to send emails on your behalf</li>
                <li>You'll be redirected back to this page after authorization</li>
                <li>Send a test email to verify everything works</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EmailSettings;
